// sessionStorage = data is cleared when the page session ends.
// localStorage = data is bounded to the site, it does not expires
import { Logger, NOOP_VOID } from 'cube'

import { jwtDecode } from 'jwt-decode'
import { ServiceClient } from 'bula-certa-commons/service'

export type AccessTokenAboutToExpire = (sender: AppStorage, token: string) => void

type VoidListenerType = () => void

type Authorization = {
    token: string
}

const LOG = Logger.get('AppStorage')

type StorageProperty<T> = {
    get: () => T
    set: (value: unknown, rememberMe?: boolean) => void
    clear: () => void
}

const EXPIRATION_DATE_IN_MILLIS = Date.now()

export class AppStorage {

    private __eventIdGen = 0

    private __tokenAbouteToExpireMap = new Map<number, AccessTokenAboutToExpire>()
    private __tokenAboutToExpireTimeHandler: VoidListenerType = NOOP_VOID

    private __rememberMeProp = newLocalStorageBoolProperty('3ae9b5f1')
    private __accesssTokenProp = newStorageStringProperty('a9c07324')
    

    get rememberMe() {
        return this.__rememberMeProp.get()
    }

    get accessTokenExpiration() {
        try {
            const accessToken = this.accessToken
            if (!accessToken) {
                return EXPIRATION_DATE_IN_MILLIS
            }

            const info = decodeAccessToken(accessToken)

            return info.exp * 1000
        } catch (err) {
            LOG.error('[accessTokenExpirationDate]', err)
            return EXPIRATION_DATE_IN_MILLIS
        }
    }

    get accessToken() {
        return this.__accesssTokenProp.get()
    }

    clearAccessToken() {
        this.__rememberMeProp.clear()
        this.__accesssTokenProp.clear()


        this.__tokenAboutToExpireTimeHandler()
        this.__tokenAboutToExpireTimeHandler = NOOP_VOID
    }
    
    setAuthorization(value: Omit<Authorization, 'token'>, rememberMe: boolean) {
        this.__rememberMeProp.set(rememberMe, false)

        const token = this.__accesssTokenProp.get()
        this.launchTokenExpirationTimeout(token)
    }

    setAccessToken(value: Authorization, rememberMe: boolean) {
        this.clearAccessToken()
        if (!value || !value.token) {
            return
        }
        this.__accesssTokenProp.set(value.token, rememberMe)
        this.setAuthorization(value, rememberMe)
    }

    onAccessTokenAboutToExpire(listener: AccessTokenAboutToExpire) {
        const eventId = this.__eventIdGen++
        this.__tokenAbouteToExpireMap.set(eventId, listener)

        const accessToken = this.accessToken
        if (accessToken) {
            this.launchTokenExpirationTimeout(accessToken)
        } else {
            this.__tokenAboutToExpireTimeHandler()
            this.__tokenAboutToExpireTimeHandler = NOOP_VOID
        }

        return () => {
            this.__tokenAbouteToExpireMap.delete(eventId)
        }
    }

    private launchTokenExpirationTimeout(accessToken: string) {
        this.__tokenAboutToExpireTimeHandler()
        this.__tokenAboutToExpireTimeHandler = NOOP_VOID

        const info = decodeAccessToken(accessToken)
        const millisToExpire = differenceInMilliseconds(info.exp * 1000, Date.now())
        if (millisToExpire > 0) {
            const refreshToken = () => {
                for (const listener of this.__tokenAbouteToExpireMap.values()) {
                    try {
                        listener(this, accessToken)
                    } catch (err) {
                        LOG.error(`Processing listner ${listener}`, err)
                    }
                }
            }

            const oneHourInMillis = minutesToMilliseconds(60)

            if (millisToExpire < oneHourInMillis) {
                // Do it now
                refreshToken()
            }

            // And, refresh at fix rate of one hour
            this.__tokenAboutToExpireTimeHandler = scheduleAtFixRate(refreshToken, oneHourInMillis)
        }
    }
}

const APP_STORAGE_INSTANCE = new AppStorage()

ServiceClient.setTokenProvider(() => APP_STORAGE_INSTANCE.accessToken)

export default APP_STORAGE_INSTANCE

// :: Internal

function decodeAccessToken(accessToken: string) {
    return jwtDecode(accessToken) as {
        username: string
        sub: number
        iat: number
        exp: number
    }
}

function scheduleAtFixRate(listner: VoidListenerType, interval: number) {
    const ctx = {
        handler: undefined as number | undefined,
        stoped: false
    }

    const relauchListener: VoidListenerType = () => {
        if (ctx.stoped) {
            return
        }

        try {
            ctx.handler = undefined
            listner()
        } catch (err) {
            LOG.error('[scheduleAtFixRate]', err)
        } finally {
            ctx.handler = setTimeout(relauchListener, interval)
        }
    }

    ctx.handler = setTimeout(relauchListener, interval)

    return () => {
        ctx.stoped = true
        if (ctx.handler) {
            clearTimeout(ctx.handler)
            ctx.handler = undefined
        }
    }
}

function newLocalStorageBoolProperty(uid: string): StorageProperty<boolean> {
    return {
        set: (value: unknown) => {
            localStorage.setItem(uid, value ? 'true' : 'false')
        },
        get: () => {
            return localStorage.getItem(uid) === 'true'
        },
        clear: () => {
            localStorage.removeItem(uid)
        }
    }
}

function newStorageStringProperty(uid: string): StorageProperty<string> {
    return {
        set: (value: unknown, rememberMe?: boolean) => {
            if (rememberMe) {
                if (value) {
                    localStorage.setItem(uid, `${value}`)
                } else {
                    localStorage.removeItem(uid)
                }

                sessionStorage.removeItem(uid)
            } else {
                if (value) {
                    sessionStorage.setItem(uid, `${value}`)
                } else {
                    sessionStorage.removeItem(uid)
                }
                localStorage.removeItem(uid)
            }
        },
        get: () => {
            return localStorage.getItem(uid) || sessionStorage.getItem(uid) || ''
        },
        clear: () => {
            localStorage.removeItem(uid)
            sessionStorage.removeItem(uid)
        }
    }
}

function differenceInMilliseconds(a: number, b: number) {
    return a - b
}

function minutesToMilliseconds(minutes: number) {
    return minutes * 60 * 1000
}